import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {ResultType} from 'omnibox/omniboxTypes';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import APIUtil from 'util/APIUtil';

export type FilterMode = 'type' | 'project' | 'user';

export const MAX_TYPEAHEAD_RESULTS = 50;
export const UNIFIED_MAX_PER_CATEGORY = 5;
export const RECENT_ITEMS_LIMIT = 5;
export const RECENT_SEARCHES_LIMIT = 10;

// Group name for streamed search results. Retained as a semantic value so CLICKED_RESULT analytics can
// report which group a clicked result belonged to, but its visual section header is intentionally not
// rendered in Omnibox (per Figma — search results sit directly under the input with no header).
export const SEARCH_RESULTS_GROUP = 'Search Results';

// Type-toggle filter tabs (Entry / Entity / File). Each maps to one or more backend
// OmniboxObjectType strings (see benchling/llm/omnibox/omnibox_object_types.py). These ids
// become the `objectTypes` sent to search. "Entity" spans the entity family; the backend
// TypesFilter auto-expands "Sequence" into its DNA/RNA + oligo subtypes.
export type TypeToggleId = 'entry' | 'entity' | 'file';

const TYPE_TOGGLE_OBJECT_TYPES: Record<TypeToggleId, ReadonlyArray<{id: string; name: string}>> = {
  entry: [{id: 'Entry', name: 'Entry'}],
  file: [{id: 'File', name: 'File'}],
  entity: [
    {id: 'Custom Entity', name: 'Custom Entity'},
    {id: 'Sequence', name: 'Sequence'},
    {id: 'Protein', name: 'Protein'},
    {id: 'Mixture', name: 'Mixture'},
    {id: 'Molecule', name: 'Molecule'},
  ],
};

export function getTypeToggleFilters(toggle: TypeToggleId): Array<{kind: 'type'; id: string; name: string}> {
  return TYPE_TOGGLE_OBJECT_TYPES[toggle].map((t) => ({kind: 'type', id: t.id, name: t.name}));
}

// Maps a type toggle to the ModelTypeConstants used by the full search page's TYPES filter (see
// ALL_LISTABLE_TYPES in search/SearchConstants). This is a separate vocabulary from the backend
// OmniboxObjectType strings above: "entity" enumerates the entity family (the omnibox "Sequence"
// object type expands to its DNA/RNA + oligo subtypes here) so the search page restores an
// equivalent filter when opened from the omnibox.
const TYPE_TOGGLE_MODEL_TYPES: Record<TypeToggleId, ReadonlyArray<ModelTypeConstants>> = {
  entry: [ModelTypeConstants.ENTRY],
  file: [ModelTypeConstants.FILE],
  entity: [
    ModelTypeConstants.BASIC_FOLDER_ITEM,
    ModelTypeConstants.SEQUENCE,
    ModelTypeConstants.RNA_SEQUENCE,
    ModelTypeConstants.DNA_OLIGO,
    ModelTypeConstants.RNA_OLIGO,
    ModelTypeConstants.PROTEIN,
    ModelTypeConstants.ANTIBODY,
    ModelTypeConstants.OLIGO_CONJUGATE,
    ModelTypeConstants.OLIGO_DUPLEX,
    ModelTypeConstants.MIXTURE,
    ModelTypeConstants.MOLECULE,
  ],
};

export function getTypeToggleModelTypes(toggle: TypeToggleId): ReadonlyArray<ModelTypeConstants> {
  return TYPE_TOGGLE_MODEL_TYPES[toggle];
}

// Maps ModelTypeConstants enum values to display names for API-ID-based resolution.
// See also DOMAIN_CLASS_TO_DISPLAY_NAME in OmniboxAction.tsx which maps backend domain
// graph class names (PascalCase) to display names for search result normalization.
const MODEL_TYPE_DISPLAY_NAMES: Partial<Record<ModelTypeConstants, string>> = {
  [ModelTypeConstants.ENTRY]: 'Entry',
  [ModelTypeConstants.SEQUENCE]: 'DNA Sequence',
  [ModelTypeConstants.RNA_SEQUENCE]: 'RNA Sequence',
  [ModelTypeConstants.DNA_OLIGO]: 'DNA Oligo',
  [ModelTypeConstants.RNA_OLIGO]: 'RNA Oligo',
  [ModelTypeConstants.PROTEIN]: 'Protein',
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: 'Custom Entity',
  [ModelTypeConstants.MIXTURE]: 'Mixture',
  [ModelTypeConstants.MOLECULE]: 'Molecule',
  [ModelTypeConstants.ANTIBODY]: 'Protein',
  [ModelTypeConstants.EQUIPMENT]: 'Equipment',
  [ModelTypeConstants.OLIGO_CONJUGATE]: 'Oligo Conjugate',
  [ModelTypeConstants.OLIGO_DUPLEX]: 'Oligo Duplex',
  [ModelTypeConstants.CONTAINER]: 'Container',
  [ModelTypeConstants.PLATE]: 'Plate',
  [ModelTypeConstants.BOX]: 'Box',
  [ModelTypeConstants.LOCATION]: 'Location',
  [ModelTypeConstants.PROCESS]: 'Workflow Task Group',
  [ModelTypeConstants.PROCESS_TASK]: 'Workflow Task',
  [ModelTypeConstants.PROCESS_OUTPUT]: 'Workflow Output',
  [ModelTypeConstants.STAGE_ENTRY]: 'Stage Entry',
  [ModelTypeConstants.FOLDER]: 'Folder',
  [ModelTypeConstants.REQUEST]: 'Request',
  [ModelTypeConstants.REQUEST_V2_SUBMISSION]: 'Request',
  [ModelTypeConstants.STUDY]: 'Study',
  [ModelTypeConstants.PROTOCOL]: 'Protocol',
  [ModelTypeConstants.TEMPLATE]: 'Template',
  [ModelTypeConstants.TEMPLATE_COLLECTION]: 'Template Collection',
  [ModelTypeConstants.BULK_ASSEMBLY]: 'Assembly',
  [ModelTypeConstants.ASSAY_RUN]: 'Run',
  [ModelTypeConstants.DATASET]: 'Dataset',
  [ModelTypeConstants.FILE]: 'File',
  [ModelTypeConstants.ANALYSIS]: 'Analysis',
  [ModelTypeConstants.ANALYSIS_TEMPLATE]: 'Automation Template',
  [ModelTypeConstants.ANALYTICS_DASHBOARD]: 'Dashboard',
  [ModelTypeConstants.SEQUENCE_ANALYSIS]: 'DNA/RNA Alignment',
  [ModelTypeConstants.PROTEIN_ALIGNMENT]: 'AA Alignment',
  [ModelTypeConstants.CONNECTION]: 'Connection',
  [ModelTypeConstants.WORKLIST]: 'Worklist',
  [ModelTypeConstants.WORKSHEET]: 'Worksheet',
};

export function getObjectTypeDisplayName(apiId: string): string | undefined {
  const modelType = APIUtil.apiIdToMaybeType(apiId);
  return modelType ? MODEL_TYPE_DISPLAY_NAMES[modelType] : undefined;
}

export function getModelTypeDisplayName(modelType: string): string | undefined {
  return MODEL_TYPE_DISPLAY_NAMES[modelType as ModelTypeConstants];
}

export const FILTER_ACTION_META: Record<
  OmniboxFilter['kind'],
  {group: string; resultType: ResultType; order: number}
> = {
  type: {group: 'Types', resultType: 'type_suggestion', order: 0},
  schema: {group: 'Schemas', resultType: 'schema_suggestion', order: 1},
  project: {group: 'Projects & Folders', resultType: 'project_suggestion', order: 2},
  folder: {group: 'Projects & Folders', resultType: 'folder_suggestion', order: 3},
  user: {group: 'People', resultType: 'user_suggestion', order: 4},
};

export const NOTHING_FOUND_MESSAGES: Record<FilterMode, string> = {
  type: 'No matching types or schemas',
  project: 'No matching projects or folders',
  user: 'No matching people',
};

export const FILTER_MODE_PLACEHOLDERS: Record<FilterMode, string> = {
  type: 'Filter by type or schema...',
  project: 'Filter by folder...',
  user: 'Filter by author...',
};
